import Accordion from 'components/Accordion'
import { THeaderType } from 'components/Header/Header'
import { TLocaleCode } from 'constants/locales'
import React, { FC } from 'react'
import { BadgeType } from 'src/types'
import styled from 'styled-components'
import BadgeWarning from 'ui/Badge'
import Ul from 'ui/Ul'
import BadgeDropdown from '../BadgeDropdown/BadgeDropdown'
import NavItem from '../NavItem'

const NavBarAccordion: FC<Props> = ({ name, onClose, iconLink, badgeType, warning, options, type, locale }) => {
  return (
    <Accordion
      renderButton={(onToggle, isActive) => (
        <AccordionButton $type={type} $isActive={isActive} onClick={onToggle}>
          {iconLink && <IconContainer>{iconLink}</IconContainer>}
          {name}
          <BadgeDropdown type={badgeType} />

          {warning && <BadgeWarningWrapper>{warning}</BadgeWarningWrapper>}
        </AccordionButton>
      )}
      renderContent={() => {
        return (
          <Ul>
            {options.map(({ name, url, badgeType, includeLangsArr, showChat, component }) => {
              if (component) {
                return component()
              }

              return (
                <NavItemContainer $type={type} key={url}>
                  <NavItem
                    withLocale={locale}
                    onClick={() => {
                      showChat && showChat(true)
                      onClose && onClose()
                    }}
                    badgeType={badgeType}
                    to={url}
                    name={name}
                    type={type}
                    includeLangsArr={includeLangsArr}
                  />
                </NavItemContainer>
              )
            })}
          </Ul>
        )
      }}
    />
  )
}

export default NavBarAccordion

const NavItemContainer = styled.li<{ $type: THeaderType }>`
  padding-inline-start: 40px;
  border-bottom: 1px solid rgba(48, 113, 238, 0.1);
  -webkit-tap-highlight-color: transparent;

  :hover {
    background-color: ${({ $type, theme: { colors } }) =>
      $type === 'primary' ? colors.white : `rgba(48, 113, 238, 0.2)`};

    & > a {
      color: ${({ $type, theme: { colors } }) => ($type === 'primary' ? colors.newLightColor.vividBlue : colors.white)};
    }
  }
`

const AccordionButton = styled.div<{ $isActive: boolean; $type: THeaderType }>`
  display: flex;
  align-items: center;
  color: ${({ $isActive, $type, theme: { colors } }) =>
    $type === 'primary'
      ? $isActive
        ? colors.newLightColor.vividBlue
        : colors.newLightColor.softNavy
      : $isActive
      ? colors.newDarkColor.white
      : colors.lightBlue};

  font-size: 14px;
  font-weight: ${({ theme: { fontWeight } }) => fontWeight.semibold};
  -webkit-tap-highlight-color: transparent;
  padding: 16px 40px;

  &::after {
    display: inline-block;
    vertical-align: 3.96px;
    content: '';
    width: 9px;
    height: 6px;
    margin-inline-start: auto;
    border-style: solid;
    border-width: 6px 5px 0 5px;
    border-color: #6b7b9f transparent transparent transparent;
    transition: all 0.1s ease-out;
  }
  ${({ $isActive, $type, theme: { colors } }) =>
    $isActive &&
    `
    border-bottom: 1px solid ${$type === 'primary' ? '#b9cff9' : '#1f4da9'};

  ::after {
    transition: all 0.1s ease-out;
    border-color: ${
      $type === 'primary' ? '#97b8f7' : colors.newDarkColor.vividBlue
    } transparent transparent transparent;
    transform: rotate(180deg);
  }
`}

  :hover {
    color: ${({ $type, theme: { colors } }) => ($type === 'primary' ? colors.newLightColor.vividBlue : colors.white)};

    background-color: ${({ $type, theme: { colors } }) =>
      $type === 'primary' ? colors.white : `rgba(48, 113, 238, 0.2)`};
  }
`

const IconContainer = styled.div`
  width: 24px;
  height: 24px;
  margin-inline-end: 1rem;
`

const BadgeWarningWrapper = styled(BadgeWarning)`
  margin-inline-start: 0.5em;
`

interface Props {
  name?: string
  warning?: string | null
  options: Array<{
    name: string
    url: string
    badgeType?: BadgeType | null
    includeLangsArr?: TLocaleCode[]
    showChat?: (id: boolean) => void
    component?: () => JSX.Element
  }>
  type: THeaderType
  iconLink?: JSX.Element
  badgeType?: BadgeType | null
  onClose?: () => void
  locale?: boolean
}
